import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Radio } from "@/components/Radio/Radio";
import { classNames } from "@/utils/classNames";

export type OpenInReviewScope = "selected" | "filtered" | "tab" | "all";

export interface OpenInReviewScopeCounts {
	selected: number;
	filtered: number | null;
	tab: number | null;
	all: number | null;
}

interface OpenInReviewModalProps {
	isOpened: boolean;
	currentTabLabel: string;
	counts: OpenInReviewScopeCounts;
	isConfirming?: boolean;
	title?: string;
	subtitle?: string;
	submitButtonText?: string;
	onClose: () => void;
	onConfirm: (scope: OpenInReviewScope) => void;
}

export const OpenInReviewModal: FunctionComponent<OpenInReviewModalProps> = ({
	isOpened,
	currentTabLabel,
	counts,
	isConfirming = false,
	title,
	subtitle,
	submitButtonText,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`list.openInReview.${key}`, options),
		[t]
	);

	const defaultScope: OpenInReviewScope = useMemo(
		() => (counts.selected > 0 ? "selected" : "filtered"),
		[counts.selected]
	);

	const [scope, setScope] = useState<OpenInReviewScope>(defaultScope);

	useEffect(() => {
		if (isOpened) {
			setScope(defaultScope);
		}
	}, [isOpened, defaultScope]);

	const options: {
		value: OpenInReviewScope;
		label: string;
		description: string;
		count: number | null;
		disabled?: boolean;
	}[] = [
		{
			value: "selected",
			label: ts("scopeSelected"),
			description: ts("scopeSelectedDesc"),
			count: counts.selected,
			disabled: counts.selected <= 0,
		},
		{
			value: "filtered",
			label: ts("scopeFiltered"),
			description: ts("scopeFilteredDesc"),
			count: counts.filtered,
		},
		{
			value: "tab",
			label: ts("scopeTab", { tab: currentTabLabel }),
			description: ts("scopeTabDesc"),
			count: counts.tab,
		},
		{
			value: "all",
			label: ts("scopeAll"),
			description: ts("scopeAllDesc"),
			count: counts.all,
		},
	];

	return (
		<Modal
			cancelButtonText={t("basics.cancel")}
			handleClose={onClose}
			handleSave={() => onConfirm(scope)}
			isLoading={isConfirming}
			isOpened={isOpened}
			size="sm"
			submitButtonText={submitButtonText ?? ts("confirm")}
			subtitle={subtitle ?? ts("subtitle")}
			title={title ?? ts("title")}
		>
			<div className="flex flex-col gap-2" data-testid="open-in-review-modal">
				{options.map((option) => {
					const isSelected = scope === option.value;

					return (
						<button
							key={option.value}
							className={classNames(
								"flex items-start gap-3 w-full text-left p-3 rounded-md border transition-colors",
								option.disabled
									? "border-border-light opacity-50 cursor-not-allowed"
									: isSelected
										? "border-blue-action bg-light-blue cursor-pointer"
										: "border-border-light hover:border-blue-action cursor-pointer"
							)}
							data-testid={`open-in-review-option-${option.value}`}
							disabled={option.disabled}
							type="button"
							onClick={() => !option.disabled && setScope(option.value)}
						>
							<div className="pt-0.5">
								<Radio isChecked={isSelected} />
							</div>
							<div className="flex-1">
								<div className="flex items-center gap-2 text-sm font-medium text-text">
									<span>{option.label}</span>
									{option.count !== null && (
										<span className="text-xs text-text-meta font-normal">
											{option.count.toLocaleString()}
										</span>
									)}
								</div>
								<div className="text-xs text-text-meta mt-0.5">{option.description}</div>
							</div>
						</button>
					);
				})}
			</div>
		</Modal>
	);
};
